import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../lib/utils';
import { useLanguage } from '../context/LanguageContext';
import TrustStamp from './TrustStamp';

export default function FluidSubpageHero({
  eyebrow,
  title,
  description,
  imageUrl,
  imageAlt = '',
  ctaText,
  ctaLink,
  ctaIcon,
  secondaryCtaText,
  secondaryCtaLink,
  secondaryCtaIcon,
  tertiaryCtaText,
  tertiaryCtaLink,
  tertiaryCtaIcon,
  badge,
  children,
  organicShape = true,
  imageFit = 'cover',
  noHover = false,
  hasFrame = false,
  noBox = false,
  hideBackdrop = false,
  hasMask = false,
  imageClass = '',
  className = '',
  containerClassName = '',
  textContainerClass = '',
  bgImageUrl,
  bgImageAlt = '',
  bgImageClass = '',
  bgOverlayClass = '',
  showBgOverlay = true,
  mobileShowBgBelow = false,
  mobileBgImageUrl,
  mobileBgImageAlt = '',
  mobileBgImageClass = '',
  dark = false,
  titleClass = '',
  eyebrowClass = '',
  descriptionClass = '',
  dividerClass = '',
  style,
  reviewStamp,
  aspectSquare = false,
  hasBorder = true
}) {
  const { localizePath } = useLanguage();
  const hasCustomBg = className?.includes('bg-') || Boolean(style?.backgroundColor || style?.background);

  return (
    <section
      style={style}
      className={cn(
        'relative w-full overflow-hidden flex items-center',
        mobileShowBgBelow
          ? 'pt-10 pb-6 sm:py-20 lg:py-24'
          : 'py-14 sm:py-20 lg:py-24',
        dark
          ? 'bg-[#0f2420] text-white border-b border-[#1c3c35]'
          : hasCustomBg
            ? 'border-b border-sand-medium/40'
            : 'bg-sand-light border-b border-sand-medium/40',
        imageUrl || bgImageUrl
          ? mobileShowBgBelow
            ? 'min-h-auto sm:min-h-[620px] lg:min-h-[680px]'
            : 'min-h-[560px] sm:min-h-[620px] lg:min-h-[680px]'
          : 'min-h-[400px] lg:min-h-[460px]',
        className
      )}
    >
      {/* Full Background Image if provided (Desktop / standard or full background) */}
      {bgImageUrl && (
        <div
          className={cn(
            "overflow-hidden pointer-events-none",
            mobileShowBgBelow
              ? "hidden sm:block absolute inset-0 z-0"
              : "absolute inset-0 z-0"
          )}
        >
          <img
            src={bgImageUrl}
            alt={bgImageAlt || ''}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className={cn('w-full h-full object-cover object-center', bgImageClass)}
          />
          {showBgOverlay && (
            <div
              className={cn(
                'absolute inset-0',
                bgOverlayClass
                  ? bgOverlayClass
                  : dark
                    ? 'bg-gradient-to-r from-[#0c241f]/90 via-[#0c241f]/60 to-transparent sm:from-[#0c241f]/85 sm:via-[#0c241f]/50 sm:to-transparent'
                    : 'bg-gradient-to-r from-sand-light/95 via-sand-light/85 to-sand-light/65 sm:from-sand-light/90 sm:via-sand-light/80 sm:to-sand-light/50 backdrop-blur-[1px]'
              )}
            />
          )}
        </div>
      )}

      {/* Ambient background accent */}
      {!bgImageUrl && (
        <div className="absolute top-1/2 right-12 -translate-y-1/2 w-96 h-96 bg-[#D1E1BE]/30 rounded-full blur-3xl pointer-events-none" />
      )}

      {/* Hero Content Grid */}
      <div className={cn('relative z-10 w-full mx-auto px-6', containerClassName || 'max-w-[1180px]')}>
        <div className={`grid grid-cols-1 ${imageUrl ? 'lg:grid-cols-12 gap-8 lg:gap-12 items-center' : 'max-w-3xl'}`}>
          
          {/* Hero Text Column */}
          <div className={imageUrl ? 'lg:col-span-6 xl:col-span-6' : 'w-full'}>
            <div className={cn("relative z-10", textContainerClass)}>
              
              {badge && (
                <div className="mb-4">
                  {badge}
                </div>
              )}

              {eyebrow && !badge && (
                <span
                  className={cn(
                    "inline-block text-xs font-bold uppercase tracking-[0.16em] mb-3 break-words",
                    dark ? "text-[#D1E1BE]" : "text-petrol-light",
                    eyebrowClass
                  )}
                >
                  {eyebrow}
                </span>
              )}

              <h1
                className={cn(
                  "font-display text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight leading-[1.15] mb-4 whitespace-pre-line break-words",
                  dark ? "text-white" : "text-petrol-dark",
                  titleClass
                )}
              >
                {title}
              </h1>

              {/* Accent Divider */}
              <div className={cn("h-[2px] w-10 bg-[#D1E1BE] rounded-full my-4", dividerClass)} />

              {description && (
                typeof description === 'string' && /<[a-z][\s\S]*>/i.test(description) ? (
                  <div 
                    className={cn(
                      "font-sans text-base sm:text-lg leading-relaxed font-normal mb-6 [&>p]:mb-3 [&>p:last-child]:mb-0",
                      dark ? "text-white/90" : "text-text-dark/85",
                      descriptionClass
                    )}
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                ) : (
                  <p
                    className={cn(
                      "font-sans text-base sm:text-lg leading-relaxed font-normal mb-6",
                      dark ? "text-white/90" : "text-text-dark/85",
                      descriptionClass
                    )}
                  >
                    {description}
                  </p>
                )
              )}

              {(ctaText && ctaLink) || (secondaryCtaText && secondaryCtaLink) || (tertiaryCtaText && tertiaryCtaLink) ? (
                <div className="pt-2 flex flex-wrap items-center gap-3 sm:gap-4">
                  {ctaText && ctaLink && (
                    typeof ctaLink === 'string' && (ctaLink.startsWith('http://') || ctaLink.startsWith('https://') || ctaLink.startsWith('mailto:') || ctaLink.startsWith('tel:')) ? (
                      <a
                        href={ctaLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center rounded-full bg-petrol-dark text-white px-7 py-3.5 text-sm font-semibold hover:bg-petrol-medium transition-all duration-200 gap-2.5 shadow-sm hover:shadow-md cursor-pointer"
                      >
                        <span>{ctaText}</span>
                        {ctaIcon ? ctaIcon : null}
                      </a>
                    ) : (
                      <Link
                        to={ctaLink}
                        className="inline-flex items-center justify-center rounded-full bg-petrol-dark text-white px-7 py-3.5 text-sm font-semibold hover:bg-petrol-medium transition-all duration-200 gap-2.5 shadow-sm hover:shadow-md cursor-pointer"
                      >
                        <span>{ctaText}</span>
                        {ctaIcon ? ctaIcon : null}
                      </Link>
                    )
                  )}

                  {secondaryCtaText && secondaryCtaLink && (
                    <Link
                      to={localizePath(secondaryCtaLink)}
                      className={cn(
                        "inline-flex items-center justify-center rounded-full px-7 py-3.5 text-sm font-semibold transition-all duration-200 gap-2.5 cursor-pointer",
                        dark
                          ? "bg-transparent hover:bg-white/10 text-white border border-white/40"
                          : "bg-transparent hover:bg-petrol-dark/5 text-petrol-dark border border-petrol-dark/60 hover:border-petrol-dark hover:text-petrol-dark"
                      )}
                    >
                      <span>{secondaryCtaText}</span>
                      {secondaryCtaIcon ? secondaryCtaIcon : null}
                    </Link>
                  )}

                  {tertiaryCtaText && tertiaryCtaLink && (
                    <Link
                      to={localizePath(tertiaryCtaLink)}
                      className={cn(
                        "inline-flex items-center justify-center rounded-full px-7 py-3.5 text-sm font-semibold transition-all duration-200 gap-2.5 cursor-pointer",
                        dark
                          ? "bg-transparent hover:bg-white/10 text-white border border-white/40"
                          : "bg-transparent hover:bg-petrol-dark/5 text-petrol-dark border border-petrol-dark/60 hover:border-petrol-dark hover:text-petrol-dark"
                      )}
                    >
                      <span>{tertiaryCtaText}</span>
                      {tertiaryCtaIcon ? tertiaryCtaIcon : null}
                    </Link>
                  )}
                </div>
              ) : null}

              {/* Mobile hero image displayed below hero content if mobileShowBgBelow is active */}
              {(mobileBgImageUrl || bgImageUrl) && mobileShowBgBelow && (
                <div className="block sm:hidden mt-8 w-full">
                  <div className={cn(
                    "relative w-full max-w-md mx-auto overflow-hidden",
                    mobileBgImageUrl
                      ? "aspect-[16/10] rounded-2xl shadow-xs border border-sand-medium/40 bg-sand-medium/10"
                      : "aspect-[4/3] rounded-2xl flex items-center justify-center"
                  )}>
                    <img
                      src={mobileBgImageUrl || bgImageUrl}
                      alt={mobileBgImageAlt || bgImageAlt || ''}
                      loading="eager"
                      fetchPriority="high"
                      decoding="async"
                      className={cn(
                        mobileBgImageUrl
                          ? "w-full h-full object-cover object-center"
                          : "w-full h-full object-contain object-center mix-blend-multiply",
                        mobileBgImageClass
                      )}
                    />
                  </div>
                </div>
              )}

              {children}

              {/* Fagligt gennemgået / Review trust stamp */}
              {reviewStamp && (
                <TrustStamp text={reviewStamp} dark={dark} />
              )}
            </div>
          </div>

          {/* Right Column: Image Container */}
          {imageUrl && (
            <div className="lg:col-span-6 xl:col-span-6 flex items-center justify-center lg:justify-end self-center">
              <div className={`relative w-full ${
                noBox 
                  ? 'max-w-4xl lg:max-w-5xl xl:max-w-6xl scale-105 sm:scale-115 lg:scale-125 xl:scale-135' 
                  : aspectSquare
                  ? 'max-w-[500px] sm:max-w-[540px] lg:max-w-[580px] xl:max-w-[620px] aspect-square'
                  : 'max-w-3xl lg:max-w-4xl xl:max-w-5xl aspect-[4/3] sm:aspect-[1.35/1]'
              } my-2 group`}>
                
                {/* Grey Shape behind Image */}
                {!noBox && !hideBackdrop && (
                  <div 
                    className={`absolute -inset-3 sm:-inset-4 lg:-inset-5 bg-[#e2dad0]/80 sm:bg-[#ded7cc]/90 opacity-90 pointer-events-none transition-transform duration-700 group-hover:scale-[1.02] z-0 ${
                      organicShape ? '' : 'rounded-2xl sm:rounded-3xl'
                    }`}
                    style={
                      organicShape
                        ? {
                            borderRadius: '62% 38% 68% 32% / 40% 58% 42% 60%',
                            transform: 'rotate(-2deg)'
                          }
                        : undefined
                    }
                  />
                )}

                <div 
                  className={`relative z-10 w-full h-full overflow-hidden ${
                    noBox
                      ? ''
                      : organicShape 
                      ? (hasBorder ? 'border border-sand-medium/30' : '') 
                      : hasFrame 
                      ? 'rounded-2xl sm:rounded-3xl bg-white/60 border border-sand-medium/50 p-2 sm:p-3' 
                      : hasBorder 
                      ? 'rounded-2xl sm:rounded-3xl border border-sand-medium/40' 
                      : 'rounded-2xl sm:rounded-3xl'
                  }`}
                  style={
                    organicShape && !noBox 
                      ? { borderRadius: '60% 40% 70% 30% / 45% 62% 28% 72%' } 
                      : undefined
                  }
                >
                  <img 
                    src={imageUrl} 
                    alt={imageAlt} 
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    className={`w-full h-full ${
                      imageFit === 'contain' ? 'object-contain' : 'object-cover'
                    } object-center ${
                      organicShape ? 'grayscale-[20%] saturate-[0.75] contrast-[1.02] mix-blend-multiply' : ''
                    } ${imageClass} ${
                      noHover || !organicShape ? '' : 'scale-[1.18] transition-transform duration-700 group-hover:scale-[1.24]'
                    }`}
                    style={
                      hasMask
                        ? {
                            maskImage: 'radial-gradient(ellipse 75% 75% at 50% 50%, black 30%, rgba(0, 0, 0, 0.8) 50%, rgba(0, 0, 0, 0.3) 65%, transparent 80%)',
                            WebkitMaskImage: 'radial-gradient(ellipse 75% 75% at 50% 50%, black 30%, rgba(0, 0, 0, 0.8) 50%, rgba(0, 0, 0, 0.3) 65%, transparent 80%)'
                          }
                        : undefined
                    }
                  />
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </section>
  );
}
